import { useState, useCallback, useEffect } from 'react';
import { darken, Paper, Typography, Grid, Box, List, ListItem, ListItemIcon, ListItemText, makeStyles } from '@material-ui/core'
import SportsKabaddiIcon from '@material-ui/icons/SportsKabaddi';
import { Link } from "react-router-dom";
import dateToString from '../../utils/dateToString';

const useStyles = makeStyles((theme) => ({
    myMatchList: {
        padding: 0,
    },
    myMatchCell: {
        '&:hover': {
            backgroundColor: darken(theme.palette.background.paper, 0.2)
        },
    },
    live: {
        color: theme.palette.error.main,
    },
    // the blinking dot of a recording indicator
    liveDot: {
        display: 'inline-block',
        width: '0.55em',
        height: '0.55em',
        marginRight: '0.4em',
        verticalAlign: 'middle',
        borderRadius: '50%',
        backgroundColor: theme.palette.error.main,
        animation: '$recording 1.2s ease-in-out infinite',
    },
    '@keyframes recording': {
        '0%, 100%': {
            opacity: 1,
        },
        '50%': {
            opacity: 0.2,
        },
    },
    // so that it does not read as the name of an opponent
    undecidedOpponent: {
        color: theme.palette.text.secondary,
    },
    countdown: {
        color: theme.palette.success.main,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 500,
    }
}));

// The time a match starts at is the one its host has set, falling back on the
// time it is scheduled for
function startTimeOf(match) {
    return match.startTime ?? match.scheduledStartTime;
}

// How far off a match is, rounded down to the coarsest unit that still says
// something: "in 2 days", "in 5 hours", "in the next hour" or "is now"
function countdownToString(startTime, currentTime) {
    const remaining = startTime - currentTime;
    if (remaining <= 0) return "is now";
    const days = Math.floor(remaining / (24 * 3600 * 1000));
    if (days >= 1) return `in ${days} ${days === 1 ? "day" : "days"}`;
    const hours = Math.floor(remaining / (3600 * 1000));
    if (hours >= 1) return `in ${hours} ${hours === 1 ? "hour" : "hours"}`;
    return "in the next hour";
}

const MyMatchesCard = props => {
    const classes = useStyles();
    const [matches, setMatches] = useState([]);
    const [currentTime, setCurrentTime] = useState(Date.now());
    const getMyUpcomingMatches = useCallback(async () => {
        let matches = await fetch(`/api/my-upcoming-matches`, {
            credentials: 'include'
        }).then(res => res.json());
        return matches;
    }, []);
    const reloadMyUpcomingMatches = useCallback(() => {
        getMyUpcomingMatches().then(matches => {
            if (Array.isArray(matches)) setMatches(matches);
        });
    }, [getMyUpcomingMatches]);
    useEffect(() => {
        reloadMyUpcomingMatches();
        // so that a match that starts turns into a live one without a reload
        const interval = setInterval(reloadMyUpcomingMatches, 60000);
        return () => clearInterval(interval);
    }, [reloadMyUpcomingMatches]);
    useEffect(() => {
        if (matches.length === 0) return;
        // the countdown is counted in hours, so it does not need to tick faster
        const interval = setInterval(() => setCurrentTime(Date.now()), 30000);
        return () => clearInterval(interval);
    }, [matches.length]);

    // A match whose time has come is the next one, whether or not its host has
    // started it yet - it is not skipped over in favour of a later match
    const nextMatch = matches.find(match => startTimeOf(match) <= currentTime)
        ?? matches.find(match => startTimeOf(match) > currentTime);

    return (
        <Paper elevation={6}>
            <Box overflow='hidden'>
                <List className={classes.myMatchList}>
                    <ListItem divider>
                        <ListItemIcon>
                            <SportsKabaddiIcon />
                        </ListItemIcon>
                        <ListItemText primary='My matches' />
                    </ListItem>
                    {
                        nextMatch &&
                        <ListItem divider>
                            <ListItemText
                                primary={
                                    <>
                                        {'Next match '}
                                        <span className={classes.countdown}>{countdownToString(startTimeOf(nextMatch), currentTime)}</span>
                                    </>
                                }
                                secondary={`${nextMatch.contestant} vs ${nextMatch.opponent ?? "TBD"}`}
                            />
                        </ListItem>
                    }
                    {
                        matches.length === 0 ?
                            <ListItem divider>
                                <ListItemText secondary='You have no upcoming matches' />
                            </ListItem>
                            :
                            matches.map(match => (
                                <ListItem
                                    button
                                    divider
                                    key={match.id}
                                    component={Link}
                                    to={"/match/" + match.id}
                                    className={classes.myMatchCell}
                                >
                                    <Grid container>
                                        <Grid item xs={5}>
                                            {
                                                // once the host has set a start time it replaces the scheduled one
                                                dateToString(startTimeOf(match))
                                            }
                                            {
                                                // a match that has started but has no winner yet is being played
                                                match.startTime != null && match.startTime <= currentTime &&
                                                <Typography variant='body2' className={classes.live}>
                                                    <span className={classes.liveDot} />
                                                    Live now
                                                </Typography>
                                            }
                                        </Grid>
                                        <Grid item xs={7}>
                                            <Typography noWrap>
                                                {`${match.contestant} vs `}
                                                {
                                                    match.opponent ??
                                                    <span className={classes.undecidedOpponent}>TBD</span>
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </ListItem>
                            ))
                    }
                </List>
            </Box>
        </Paper >
    )
}

export default MyMatchesCard;
